import api from "../../../core/api";
import { Page } from "../../../shared/models/models";
import { saveBlobAsFile } from "../../../shared/utils/fileUtils";
import {
  ChangeServiceRequest,
  CreateServiceRequest,
  ServiceRequest,
  ServiceRequestStats,
  ServiceRequestsFilters,
  ServiceRequestsFiltersQuery,
  ServiceRequestsQuery,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.servicingApiBaseUrl };
};

const serializeParams = (params: any): string => {
  const parts: string[] = [];

  Object.keys(params).forEach(key => {
    const value = params[key];

    if (Array.isArray(value)) {
      value.forEach(item => {
        parts.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return parts.join('&');
};

class ServiceRequestsApi {
  getServiceRequestsFilters(query: ServiceRequestsFiltersQuery): Promise<ServiceRequestsFilters> {
    return api.get<ServiceRequestsFilters>(`/v1/customer/service-requests/filters`, {
      ...getRequestConfig(),
      params: query,
      paramsSerializer: serializeParams
    })
      .then(x => x.data);
  }

  getServiceRequests(query: ServiceRequestsQuery): Promise<Page<ServiceRequest>> {
    return api.get<Page<ServiceRequest>>(`/v1/customer/service-requests`, {
      ...getRequestConfig(),
      params: query,
      paramsSerializer: serializeParams
    })
      .then(x => x.data);
  }

  getServiceRequest(id: string): Promise<ServiceRequest> {
    return api.get<ServiceRequest>(`/v1/customer/service-requests/${id}`, getRequestConfig()).then(x => x.data);
  }

  createServiceRequest(model: CreateServiceRequest): Promise<ServiceRequest> {
    return api.post<ServiceRequest>(`/v1/customer/service-requests`, model, getRequestConfig()).then(x => x.data);
  }

  changeServiceRequest(id: string, model: ChangeServiceRequest): Promise<any> {
    return api.put<void>(`/v1/customer/service-requests/${id}`, model, getRequestConfig()).then(x => x.data);
  }

  publishServiceRequest(id: string, visibilityCode?: number, targetPartnerIds?: string[]): Promise<any> {
    const body = visibilityCode != null
      ? { visibilityCode, targetPartnerIds: targetPartnerIds || [] }
      : null;
    return api.post<void>(`/v1/customer/service-requests/${id}/publish`, body, getRequestConfig()).then(x => x.data);
  }

  unpublishServiceRequest(id: string): Promise<any> {
    return api.post<void>(`/v1/customer/service-requests/${id}/unpublish`, null, getRequestConfig()).then(x => x.data);
  }

  archiveServiceRequest(id: string): Promise<any> {
    return api.post<void>(`/v1/customer/service-requests/${id}/archive`, null, getRequestConfig()).then(x => x.data);
  }

  unarchiveServiceRequest(id: string): Promise<any> {
    return api.post<void>(`/v1/customer/service-requests/${id}/unarchive`, null, getRequestConfig()).then(x => x.data);
  }

  reactivateServiceRequest(id: string): Promise<any> {
    return api.post<void>(`/v1/customer/service-requests/${id}/reactivate`, null, getRequestConfig()).then(x => x.data);
  }

  deleteServiceRequest(id: string): Promise<any> {
    return api.delete<void>(`/v1/customer/service-requests/${id}`, getRequestConfig()).then(x => x.data);
  }

  getStats(): Promise<ServiceRequestStats> {
    return api.get<ServiceRequestStats>(`/v1/customer/service-requests/stats`, getRequestConfig())
      .then(x => x.data);
  }

  async downloadServiceRequestPdf(serviceRequest: ServiceRequest): Promise<any> {
    const url = `/v1/customer/service-requests/${serviceRequest.id}/pdf`;
    const fileName = `${serviceRequest.internalNumber}.pdf`;

    const response = await api.get(url, {
      responseType: "blob",
      baseURL: getRequestConfig().baseURL,
    });
    saveBlobAsFile(response.data, fileName);
  }
}

export default new ServiceRequestsApi();